import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getUserProfile, updateUserProfile } from '../services/api';

const JobSeekerProfilePage = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    location: '',
    title: '',
    experience: '',
    skills: '',
    education: '',
    bio: '',
    expectedSalary: '',
    availability: 'Immediately'
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    const loadProfile = async () => {
      if (currentUser) {
        const result = await getUserProfile(currentUser.uid);
        if (result.success && result.profile) {
          setFormData({
            name: result.profile.name || currentUser.displayName || '',
            email: result.profile.email || currentUser.email || '',
            phone: result.profile.phone || '',
            location: result.profile.location || '',
            title: result.profile.title || '',
            experience: result.profile.experience || '',
            skills: Array.isArray(result.profile.skills)
              ? result.profile.skills.join(', ')
              : result.profile.skills || '',
            education: result.profile.education || '',
            bio: result.profile.bio || '',
            expectedSalary: result.profile.expectedSalary || '',
            availability: result.profile.availability || 'Immediately'
          });
        } else {
          // Set default values from currentUser
          setFormData(prev => ({
            ...prev,
            name: currentUser.displayName || '',
            email: currentUser.email || ''
          }));
        }
      }
    };

    loadProfile();
  }, [currentUser]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    // Convert skills string to array
    const skillsArray = formData.skills
      .split(',')
      .map(skill => skill.trim())
      .filter(skill => skill.length > 0);

    const profileData = {
      ...formData,
      skills: skillsArray,
      userId: currentUser.uid,
      profileImage: currentUser.photoURL || 'https://via.placeholder.com/150'
    };

    const result = await updateUserProfile(currentUser.uid, profileData);

    if (result.success) {
      setMessage('Profile updated successfully!');
      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);
    } else {
      setMessage('Error updating profile. Please try again.');
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 py-8">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="bg-white rounded-xl shadow-2xl p-8 border-t-4 border-blue-500">
          <div className="flex items-center mb-6">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-4 rounded-xl mr-4">
              <span className="text-3xl">👤</span>
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-800">
                Job Seeker Profile
              </h1>
              <p className="text-gray-500">Complete your profile to get better job matches</p>
            </div>
          </div>

          {message && (
            <div className={`mb-6 p-4 rounded-lg shadow-md ${
              message.includes('success')
                ? 'bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-500 text-green-700'
                : 'bg-gradient-to-r from-red-50 to-rose-50 border-l-4 border-red-500 text-red-700'
            }`}>
              <div className="flex items-center">
                <span className="text-2xl mr-3">{message.includes('success') ? '✅' : '❌'}</span>
                <span className="font-medium">{message}</span>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Personal Information Section */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border-l-4 border-blue-500">
              <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                <span className="text-xl mr-2">📋</span>
                Personal Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    required
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Phone
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    placeholder="+1234567890"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Location
                  </label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    placeholder="City, State"
                  />
                </div>
              </div>
            </div>

            {/* Professional Information Section */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border-l-4 border-green-500">
              <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                <span className="text-xl mr-2">💼</span>
                Professional Information
              </h3>
              <div>
                <label className="block text-gray-700 font-semibold mb-2">
                  Professional Title *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all"
                  placeholder="e.g., Senior Software Engineer"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Years of Experience
                  </label>
                  <input
                    type="text"
                    name="experience"
                    value={formData.experience}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all"
                    placeholder="e.g., 5 years"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Education
                  </label>
                  <input
                    type="text"
                    name="education"
                    value={formData.education}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all"
                    placeholder="e.g., BS Computer Science - MIT"
                  />
                </div>
              </div>

              <div className="mt-4">
                <label className="block text-gray-700 font-semibold mb-2">
                  Skills (comma-separated) *
                </label>
                <input
                  type="text"
                  name="skills"
                  value={formData.skills}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all"
                  placeholder="e.g., React, Node.js, Python, AWS"
                  required
                />
                <p className="text-sm text-gray-500 mt-1">💡 Add your skills to get AI-matched job opportunities</p>
              </div>

              <div className="mt-4">
                <label className="block text-gray-700 font-semibold mb-2">
                  Bio
                </label>
                <textarea
                  name="bio"
                  value={formData.bio}
                  onChange={handleChange}
                  rows="4"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all"
                  placeholder="Tell us about yourself..."
                ></textarea>
              </div>
            </div>

            {/* Additional Information Section */}
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border-l-4 border-purple-500">
              <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                <span className="text-xl mr-2">⚙️</span>
                Job Preferences
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Expected Salary
                  </label>
                  <input
                    type="text"
                    name="expectedSalary"
                    value={formData.expectedSalary}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                    placeholder="e.g., $100,000 - $120,000"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">
                    Availability
                  </label>
                  <select
                    name="availability"
                    value={formData.availability}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                  >
                    <option value="Immediately">Immediately</option>
                    <option value="2 weeks notice">2 weeks notice</option>
                    <option value="1 month notice">1 month notice</option>
                    <option value="Not actively looking">Not actively looking</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="flex flex-col md:flex-row gap-4 pt-4">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-4 rounded-xl font-bold hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <span className="animate-spin mr-2">⏳</span>
                    Saving...
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    💾 Save Profile
                  </span>
                )}
              </button>
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="flex-1 bg-gradient-to-r from-gray-400 to-gray-500 text-white py-4 rounded-xl font-bold hover:from-gray-500 hover:to-gray-600 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                ← Back to Dashboard
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default JobSeekerProfilePage;
